import { CommonModule, DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TokenExtensions } from 'app/shared/extensions/token-extensions';
import { IActiveChat, IChatMessage, IChatUser, IChatUsers } from 'app/shared/models/chat.model';
import { ChatService } from 'app/shared/services/chat.service';
import { HelperService } from 'app/shared/services/helper.service';
import { SignalRService, SIGNAL_R_EVENTS, SIGNAL_R_LISTENERS } from 'app/shared/services/signalr.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-chat-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DatePipe, TranslateModule],
  templateUrl: './chat-dialog.component.html',
  styleUrl: './chat-dialog.component.scss'
})
export class ChatDialogComponent {
  @ViewChild('chatContainer') private chatContainer!: ElementRef;
  @Input() caseId!: number;
  chatOpened!: boolean;
  chatCollapsed!: boolean;
  joinedRoom!: number;
  @Output() onSeeAllThreads = new EventEmitter<any>();

  messages: IChatMessage[] = [];
  chatForm!: FormGroup;
  chatUsers: IChatUsers = { chatRoomName: `${this.caseId}`, users: [] };
  removedChatUsers: string[] = [];
  myUserEmail = TokenExtensions.getParsedJwt(TokenExtensions.getTokenFromLocalStorage().secret).preferred_username;
  disabledMessage = '';
  private readonly _destroy$ = new Subject<void>();

  constructor(private fb: FormBuilder, private signalRService: SignalRService, private chatService: ChatService, private router: ActivatedRoute, public helperService: HelperService) {
    this.chatForm = this.fb.group({});
    this.chatForm.addControl('message', new FormControl('', [Validators.required]));
    this.listenForChats();
  }

  ngOnInit(): void {
    this.chatService.openChat.pipe(takeUntil(this._destroy$)).subscribe((res: IActiveChat) => {
      if (res && Number(res.chatRoomName) === this.caseId) {
        if (!this.chatOpened || this.chatForm.disabled) {
          this.chatForm.enable();
          this.messages = [];
          this.joinRoom();
        }
      }
    });
    this.chatService.disableChat.pipe(takeUntil(this._destroy$)).subscribe((res: IActiveChat) => {
      if (res && Number(res.chatRoomName) === this.caseId) {
        this.chatForm.disable();
        this.disabledMessage = 'label.chat.disabled-message';
      }
    });
    this.router.params.subscribe(params => {
      this.chatCollapsed = true;
      setTimeout(() => {
        if (!this.joinedRoom || this.joinedRoom !== Number(params['id'])) {
          this.messages = [];
          this.chatForm.enable();
          this.joinRoom();
        }
        this.chatCollapsed = false;
      }, 2000);
    });
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  listenForChats(): void {
    this.signalRService.listenToChatNotificationsEvent<any>(SIGNAL_R_LISTENERS.ChatHubMessageNotification).pipe(takeUntil(this._destroy$)).subscribe((resp: IChatMessage[]) => {
      resp.forEach((message: IChatMessage) => {
        if (Number(message.chatRoomName) === this.caseId) {
          this.messages.push(message);
        }
      });
    });
    this.signalRService.listenToChatNotificationsEvent<any>(SIGNAL_R_LISTENERS.ChatHubRoomUsersNotification).pipe(takeUntil(this._destroy$)).subscribe((resp: IChatUsers) => {
      if (Number(resp.chatRoomName) === this.caseId && resp.users.length > 1) {
        this.chatForm.enable();
        this.chatUsers = resp;
      } else {
        this.chatUsers = resp;
        this.chatForm.disable();
        this.disabledMessage = 'label.chat.no-users'
      }
    });
  }

  joinRoom() {
    this.signalRService.invokeEvent(SIGNAL_R_EVENTS.JoinRoom, `${this.caseId}`).pipe(takeUntil(this._destroy$)).subscribe(resp => {
      this.joinedRoom = resp.success ? this.caseId : 0;
      this.chatOpened = resp.success;
      resp.success ? this.chatForm.enable() : this.chatForm.disable();
      this.disabledMessage = 'label.chat.disabled-message';
    });
  }

  sendMessage() {
    if (!this.messages.length) {
      this.createChat();
    } else {
      this.signalRService.invokeEvent(SIGNAL_R_EVENTS.Send, `${this.caseId}`, this.chatForm.value.message).pipe(takeUntil(this._destroy$)).subscribe(resp => {
        setTimeout(() => {
          if (resp.success) {
            this.chatForm.reset();
            this.scrollToBottom();
          }
        })
      });
    }
  }

  initiateChat() {
    this.signalRService.invokeEvent(SIGNAL_R_EVENTS.RoomUsers, `${this.caseId}`).pipe(takeUntil(this._destroy$)).subscribe(resp => {
      this.chatOpened = true;
      this.chatCollapsed = false;
      this.messages = [];
      if (!resp.success) {
        this.chatForm.disable();
        this.disabledMessage = 'label.chat.no-users'
      } else {
        this.chatForm.enable();
      }
    });
  }

  createChat() {
    let usersEmails: string[] = this.chatUsers.users.map((user: IChatUser) => { return user.email });
    usersEmails = usersEmails.filter(email => !this.isUserRemoved(email));

    this.signalRService.invokeEvent(SIGNAL_R_EVENTS.CreateRoom, `${this.caseId}`, this.chatForm.value.message, usersEmails).pipe(takeUntil(this._destroy$)).subscribe(resp => {
      if (resp.success) {
        this.chatForm.reset();
        this.scrollToBottom();
      }
    });
  }

  closeChat() {
    this.signalRService.invokeEvent(SIGNAL_R_EVENTS.CloseRoom, `${this.caseId}`).pipe(takeUntil(this._destroy$)).subscribe(a => {
      this.chatOpened = false;
    });
  }

  onKeyDown(event: any) {
    if (event.key === 'Enter' && !event.shiftKey) {
      // Prevent default behavior of adding a new line
      event.preventDefault();
      if (this.chatForm.valid) {
        this.sendMessage();
      }
    }
  }

  isMessageMy(message: IChatMessage) {
    return message.user.email === this.myUserEmail;
  }

  isUserRemoved(email: string) {
    return !!this.removedChatUsers.some((user) => user === email);
  }

  removeFromChat(email: string) {
    this.removedChatUsers.push(email);
  }

  undoRemovedUserToChat(email: string) {
    this.removedChatUsers = this.removedChatUsers.filter(user => user !== email);
  }

  isChatPossible() {
    return this.chatUsers.users.length > 1 && this.chatUsers.users.length - this.removedChatUsers.length > 1;
  }

  seeAllThreads() {
    this.onSeeAllThreads.emit(true);
  }

  private scrollToBottom(): void {
    if (this.chatOpened && !this.chatCollapsed)
      try {
        this.chatContainer.nativeElement.scrollTop =
          this.chatContainer.nativeElement.scrollHeight;
      } catch (err) {
        console.error('Scroll to bottom failed', err);
      }
  }

  ngOnDestroy(): void {
    this._destroy$.next(undefined);
    this._destroy$.complete();
  }
}
